import * as React from 'react';
import {
  Box,
  Typography,
  IconButton,
  MenuItem,
  Container,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Link,
  Modal,
  Pagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import Footer from '../Materials/Footer';
import BurgerBar from '../Materials/BurgerBar';

// Componente de Pesquisa e Filtragem
function SearchAndFilter({ onSearch, eventTypes, onSearchTextChange, searchText }) {
  const [filter, setFilter] = React.useState('');

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleSearchClick = () => {
    onSearch(filter, searchText);
  };

  return (
    <Box sx={{ p: 2, backgroundColor: '#fff', borderBottom: '1px solid #ccc' }}>
      <Container maxWidth="lg">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchText}
              onChange={(e) => onSearchTextChange(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="filter-by-label">Filtered By</InputLabel>
              <Select
                labelId="filter-by-label"
                label="Filtrar por"
                value={filter}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                {eventTypes.map((eventType) => (
                  <MenuItem key={eventType.id} value={eventType.id}>
                    {eventType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button fullWidth variant="contained" color="primary" onClick={handleSearchClick}>
              Search
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// Componente de Listagem de Eventos
function EventList() {
  const [events, setEvents] = React.useState([]);
  const [filteredEvents, setFilteredEvents] = React.useState([]);
  const [eventTypes, setEventTypes] = React.useState([]);
  const [currentFilter, setCurrentFilter] = React.useState('');
  const [searchText, setSearchText] = React.useState('');
  const [openEventModal, setOpenEventModal] = React.useState(false);
  const [selectedEvent, setSelectedEvent] = React.useState(null);

  // Paginação
  const [page, setPage] = React.useState(1);
  const eventsPerPage = 7;

  // Buscar eventos e tipos de eventos da API ao carregar o componente
  React.useEffect(() => {
    fetchEvents();
    fetchEventTypes();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/events');

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
        setFilteredEvents(data);
      } else {
        console.error("Failed to fetch events:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/eventtypes');

      if (response.ok) {
        const data = await response.json();
        setEventTypes(data);
      } else {
        console.error("Failed to fetch event types:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  const handleSearch = (filter, searchText) => {
    setCurrentFilter(filter);

    let filtered = events;

    if (filter !== '') {
      filtered = filtered.filter(event => event.event_type_id === filter);
    }

    if (searchText !== '') {
      filtered = filtered.filter(event =>
        event.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
    setPage(1);
  };

  // Paginação
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
  const startIndex = (page - 1) * eventsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, startIndex + eventsPerPage);

  // Função para formatar a data (dia e mês abreviado)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const month = monthNames[date.getMonth()];
    return { day, month };
  };

  // Função para abrir o modal com as informações do evento
  const handleOpenEventModal = (event) => {
    setSelectedEvent(event);
    setOpenEventModal(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <SearchAndFilter
        onSearch={handleSearch}
        eventTypes={eventTypes}
        onSearchTextChange={setSearchText}
        searchText={searchText}
      />

      {paginatedEvents.map((event, index) => {
        const { day, month } = formatDate(event.initial_date);

        return (
          <Box
            key={index}
            sx={{
              mb: 4,
              p: 2,
              border: '1px solid #ccc',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Quadrado azul com a data */}
            <Box
              sx={{
                backgroundColor: '#3FA5D4',
                color: 'white',
                borderRadius: '4px',
                textAlign: 'center',
                padding: '4px',
                minWidth: '40px',
                marginRight: '12px',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {day}
              </Typography>
              <Typography variant="caption">{month}</Typography>
            </Box>

            {/* Detalhes do evento */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
                {event.name}
              </Typography>

              <Typography variant="body1" sx={{ color: 'gray' }}>
                <Link href={event.link_promotor} target="_blank" rel="noopener">
                  {event.link_promotor}
                </Link>
              </Typography>
            </Box>

            {/* Ícone de "+" para abrir o modal */}
            <IconButton onClick={() => handleOpenEventModal(event)}>
              <AddIcon />
            </IconButton>
          </Box>
        );
      })}

      {/* Paginação */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Modal para exibir informações do evento */}
      <Modal open={openEventModal} onClose={() => setOpenEventModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: 4,
            boxShadow: 24,
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6">Event Details</Typography>

          {selectedEvent && (
            <>
              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Name:</strong> {selectedEvent.name}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Initial Date:</strong>{' '}
                {new Date(selectedEvent.initial_date).toLocaleDateString()}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>End Date:</strong>{' '}
                {new Date(selectedEvent.end_date).toLocaleDateString()}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Duration:</strong> {selectedEvent.duration}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Link Promotor:</strong>{' '}
                <Link href={selectedEvent.link_promotor} target="_blank" rel="noopener">
                  {selectedEvent.link_promotor}
                </Link>
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Submission Date:</strong>{' '}
                {new Date(selectedEvent.submission_date).toLocaleDateString()}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Results Divulgation Date:</strong>{' '}
                {new Date(selectedEvent.results_divulgation_date).toLocaleDateString()}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Event Type:</strong>{' '}
                {eventTypes.find(type => type.id === selectedEvent.event_type_id)?.name}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Location:</strong> {selectedEvent.location}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Description:</strong> {selectedEvent.description}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Rewards:</strong> {selectedEvent.rewards}
              </Typography>
            </>
          )}
        </Box>
      </Modal>
    </Container>
  );
}

export default function NewPage() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const navigate = useNavigate();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenDrawer(false);
  };

  const handleExit = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {/* Navbar */}
      <BurgerBar />

      {/* Listagem de Eventos com Filtragem */}
      <EventList />

      {/* Footer */}
      <Box>
        <Footer />
      </Box>
    </Box>
  );
}